/**
 * Outbound Email Templates tab: list, filters, company association. Create/edit on dedicated page.
 */
import { useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { Plus, Edit01, Trash01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Select } from "@/components/base/select/select";
import { Table, TableCard } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { Toggle } from "@/components/base/toggle/toggle";
import {
  useOutboundEmailTemplates,
  useDeleteOutboundEmailTemplate,
} from "@/hooks/api/use-outbound-email-templates";
import type { OutboundEmailTemplateStatus } from "@/types/api/outbound-email-template";

const STATUS_OPTIONS: { id: OutboundEmailTemplateStatus | ""; label: string }[] = [
  { id: "", label: "All statuses" },
  { id: "draft", label: "Draft" },
  { id: "active", label: "Active" },
  { id: "inactive", label: "Inactive" },
];

function statusColor(s: OutboundEmailTemplateStatus): "brand" | "success" | "error" | "gray" {
  switch (s) {
    case "active":
      return "success";
    case "inactive":
      return "gray";
    case "draft":
      return "brand";
    default:
      return "gray";
  }
}

function truncate(str: string | null, max: number): string {
  if (str == null) return "—";
  return str.length <= max ? str : str.slice(0, max) + "…";
}

export function OutboundEmailTemplatesTab() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<OutboundEmailTemplateStatus | "">("");
  const [systemOnly, setSystemOnly] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const filters = useMemo(
    () => ({
      ...(statusFilter ? { status: statusFilter as OutboundEmailTemplateStatus } : {}),
      system_only: systemOnly,
    }),
    [statusFilter, systemOnly]
  );

  const { data: templates = [], isLoading } = useOutboundEmailTemplates(filters);
  const deleteTemplate = useDeleteOutboundEmailTemplate();

  const handleDeleteConfirm = async (templateId: string) => {
    try {
      await deleteTemplate.mutateAsync(templateId);
      setDeleteConfirmId(null);
    } catch {
      // toast handled in hook
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-secondary bg-primary p-6">
        <h3 className="text-lg font-semibold text-primary mb-1">Outbound Email Templates</h3>
        <p className="text-tertiary text-sm mb-6">
          Create and manage email templates with dynamic tokens (aliases). Le companies associate si
          gestiscono nel form di creazione o modifica.
        </p>

        <div className="flex flex-wrap items-start gap-4 mb-6">
          <Select
            label="Status"
            items={STATUS_OPTIONS}
            selectedKey={statusFilter || " "}
            onSelectionChange={(key) => setStatusFilter((key as string) === " " ? "" : (key as OutboundEmailTemplateStatus))}
            size="md"
            className="min-w-[160px]"
          >
            {(item) => (
              <Select.Item key={item.id === "" ? " " : item.id} id={item.id === "" ? " " : item.id}>
                {item.label}
              </Select.Item>
            )}
          </Select>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-primary">Solo di sistema</span>
            <Toggle
              isSelected={systemOnly}
              onChange={() => setSystemOnly((prev) => !prev)}
              aria-label="Solo template di sistema"
            />
          </div>
          <div className="ml-auto">
            <Button
              size="md"
              color="primary"
              iconLeading={Plus}
              onClick={() => navigate("/admin/settings/outbound-email-templates/new")}
            >
              Crea template
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-tertiary">
            Caricamento...
          </div>
        ) : (
          <TableCard.Root>
            <TableCard.Header
              title="Templates"
              description={`${templates.length} template(s)`}
              contentTrailing={null}
            />
            <Table>
              <Table.Header>
                <Table.Head id="name" label="Name" isRowHeader />
                <Table.Head id="slug" label="Slug" />
                <Table.Head id="excerpt" label="Excerpt" />
                <Table.Head id="status" label="Status" />
                <Table.Head id="company" label="Company" />
                <Table.Head label="Actions" />
              </Table.Header>
              <Table.Body>
                {templates.map((t) => (
                  <Table.Row key={t.template_id}>
                    <Table.Cell>
                      <span className="text-sm font-medium text-primary">{t.name}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm text-tertiary font-mono">{t.slug}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm text-tertiary">{truncate(t.excerpt, 50)}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge color={statusColor(t.status)} size="sm">
                        {t.status}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm text-tertiary">
                        {t.company_id ? (
                          <span className="text-primary">{t.company_id}</span>
                        ) : (
                          "Sistema"
                        )}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          color="secondary"
                          iconLeading={Edit01}
                          onClick={() => navigate(`/admin/settings/outbound-email-templates/${t.template_id}/edit`)}
                        >
                          Modifica
                        </Button>
                        <Button
                          size="sm"
                          color="primary-destructive"
                          iconLeading={Trash01}
                          onClick={() => setDeleteConfirmId(t.template_id)}
                        >
                          Elimina
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            {templates.length === 0 && (
              <div className="py-12 text-center text-tertiary text-sm">
                Nessun template. Clicca &quot;Crea template&quot; per aggiungerne uno.
              </div>
            )}
          </TableCard.Root>
        )}
      </div>

      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-primary rounded-lg shadow-lg p-6 max-w-sm mx-4">
            <p className="text-primary font-medium mb-4">Eliminare questo template?</p>
            <div className="flex gap-3 justify-end">
              <Button
                size="md"
                color="secondary"
                onClick={() => setDeleteConfirmId(null)}
              >
                Annulla
              </Button>
              <Button
                size="md"
                color="primary-destructive"
                onClick={() => handleDeleteConfirm(deleteConfirmId)}
              >
                Elimina
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
